import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  IconButton,
  Loader,
  Tag,
  TagLabel,
  Button
} from '@deque/cauldron-react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useUserFlow } from '../contexts/UserFlow';
import { useAuthContext } from '../../common/contexts/auth';
import PageTitle from '../../common/components/PageTitle';
import ErrorMessage from '../../app/components/issue/ErrorMessage';
import { PRODUCT_URLS, ProductSlugs } from '../../common/constants';
import CopyToClipboard from '../../common/components/CopyToClipboard';
import styles from './UserFlowIssues.css';
import ZeroStatePanel from '../../common/components/issues/ZeroStatePanel';
import IssuesPanel from '../../common/components/issues/IssuesPanel';
import useIssuesFilter, {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import IssueFilters from '../../common/components/issues/IssueFilters';
import useSharedWith from '../hooks/useSharedWith';
import ScrimmedLoader from '../../common/components/ScrimmedLoader';
import { isNotFoundError } from '../../common/utils/errors';
import {
  buildPageStateKey,
  mapDedupedPageStatesToUniversalIssue
} from '@deque/axe-devtools-issues-utils';
import type { GetIssueURL } from '../../common/components/issues/Issues';
import { useEnterprises } from '../../common/contexts/enterprises';
import SimpleExportIssuesModal from '../components/SimpleExportIssueModal';

const UserFlowIssuesPage: React.FC = () => {
  const { loading, error, results, userFlow, updateUserFlow, updating } =
    useUserFlow();
  const { loading: authLoading, error: authError } = useAuthContext();
  const { loading: enterprisesLoading, error: enterprisesError } =
    useEnterprises();
  const { login } = useAuthContext();
  const { ready: sharedWithReady, sharedWith } = useSharedWith();
  const history = useHistory();
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement>(null);

  const [pageStateKeyTranslationLoading, setPageStateKeyTranslationLoading] =
    useState(true);

  const [showExportModal, setShowExportModal] = useState(false);

  const universalIssues = useMemo(
    () => mapDedupedPageStatesToUniversalIssue(results?.pageStateResults || []),
    [results]
  );

  const { filteredIssues, filters } = useIssuesFilter(universalIssues);

  const pageStateByFilter = useMemo(
    () =>
      filters.pageStateKey
        ? results?.pageStateResults.find(
            result => filters.pageStateKey === buildPageStateKey(result)
          )
        : null,
    [filters, results]
  );

  useEffect(() => {
    if (!updating) {
      return;
    }

    loaderRef.current?.focus();
  }, [updating, loaderRef.current]);

  /*
    To ensure backwards compatibility for UFA,
    we need to handle the searchParams appropriately.
    If pageStateKey is present, we will process issues as usual.
    However, if pageStateKey is missing but page_state_number is available,
    we must convert page_state_number to pageStateKey.
  */
  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);

    if (
      !searchParams.has('pageStateKey') &&
      searchParams.has('page_state_number')
    ) {
      const updatedSearch = new URLSearchParams(history.location.search);

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const pageStateNumber = parseInt(searchParams.get('page_state_number')!);
      const pageStateFromUrl =
        results?.pageStateResults[pageStateNumber] || null;

      if (pageStateFromUrl) {
        updatedSearch.set('pageStateKey', buildPageStateKey(pageStateFromUrl));
        updatedSearch.delete('page_state_number');
        history.replace({ search: updatedSearch.toString() });
      }
    }

    setPageStateKeyTranslationLoading(false);
  }, [history.location.search, results]);

  const getIssueURL: GetIssueURL = (issue, pagination) => {
    const url = new URL(
      `${window.location.origin}${window.location.pathname}${location.search}`
    );
    url.searchParams.append('ruleId', issue.ruleId);
    url.searchParams.append('issueIndex', `${pagination.currentPage - 1}`);
    return url.toString();
  };

  if (
    loading ||
    authLoading ||
    enterprisesLoading ||
    pageStateKeyTranslationLoading
  ) {
    return (
      <>
        <PageTitle title={t('Loading issues')} />
        <Loader label={t('Loading issues...')} />
      </>
    );
  }

  if (error && (isNotFoundError(error) || error.status === 401)) {
    return (
      <div className={styles.wrap}>
        <PageTitle title={t('User flow issues not found')} />
        <div className={styles.header}>
          <h1>{t('User flow issues not found')}</h1>
        </div>
        <p>
          <Trans>We were unable to find the user flow issues.</Trans>{' '}
          <Trans>
            You may need to{' '}
            <Button onClick={() => login()} variant="link">
              sign in
            </Button>{' '}
            or ensure you have access to the user flow issues.
          </Trans>
        </p>
      </div>
    );
  } else if (error || !results || !userFlow || authError || enterprisesError) {
    return (
      <>
        <PageTitle title={t('Error loading issues')} />
        <ErrorMessage
          error={t(
            'There was an error loading your issues. Please refresh or try again later...'
          )}
        />
      </>
    );
  }

  const additionalTags: React.ReactNode[] = [
    <Tag key="duplicates">
      <TagLabel>{t('Duplicates:')}&nbsp;</TagLabel>
      <span>{t('Hidden')}</span>
    </Tag>
  ];

  if (pageStateByFilter) {
    additionalTags.push(
      <Tag key="page-state">
        <TagLabel>{t('State:')}&nbsp;</TagLabel>
        <span>{pageStateByFilter.page_state}</span>
      </Tag>
    );
  }

  const userFlowName = userFlow.name;
  const pageTitle = t('Viewing {{userFlowName}} issues', { userFlowName });
  const testSearchParams = getSearchParamsFromFilters(getBaseFilters(filters));
  const userFlowLink = `${
    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
  }/user-flows/${userFlow.id}?${testSearchParams}`;
  const issuesLink = `${window.location.origin}${window.location.pathname}${location.search}`;
  const copyButtonLabel = t(
    'Copy link for {{userFlowName}} issues to clipboard',
    {
      userFlowName
    }
  );
  const shareButtonLabel = t(
    'Share user flow {{userFlowName}} issues and copy link to clipboard',
    {
      userFlowName
    }
  );

  const onShare = async () => {
    if (userFlow.shared_with) {
      return;
    }

    if (sharedWithReady) {
      await updateUserFlow({
        shared_with: sharedWith
      });
    }
  };

  const onExportClick = () => setShowExportModal(true);

  const noScreenshotExplanation = (
    <div>
      {t('Reasons may include:')}
      <ul>
        <li>{t('Screenshot capture is disabled in settings.')}</li>
        <li>{t('The axe core version must be at least 4.8.0.')}</li>
      </ul>
    </div>
  );

  return (
    <div className={styles.wrap}>
      {updating && (
        <ScrimmedLoader
          label={t('Sharing your User Flow analyzer issues...')}
          ref={loaderRef}
        />
      )}
      <PageTitle title={pageTitle} />
      <div className={styles.header}>
        <IconButton
          className={styles.backButton}
          icon="arrow-left"
          as={Link}
          to={userFlowLink}
          label={t('Back to {{userFlowName}}', { userFlowName })}
        />
        <h1>{pageTitle}</h1>
        <CopyToClipboard
          onCopy={onShare}
          text={issuesLink}
          confirmationMessage={t(
            'The User Flow analyzer issues link has been copied to your clipboard'
          )}
          errorMessage={t('Failed to share your User Flow analyzer issues')}
        >
          <IconButton
            icon={userFlow.shared_with ? 'link' : 'share-nodes'}
            label={userFlow.shared_with ? copyButtonLabel : shareButtonLabel}
            tooltipProps={{
              placement: 'bottom'
            }}
          />
        </CopyToClipboard>
      </div>
      <IssueFilters
        filters={filters}
        manifests={null}
        additionalTags={additionalTags}
      >
        <IconButton
          icon="export-solid"
          label={t('Export saved test and issues')}
          onClick={onExportClick}
          disabled={!results.results.length}
        />
      </IssueFilters>
      {results.results.length === 0 ? (
        <ZeroStatePanel>
          {t(
            'There are 0 total issues for {{userFlowName}} user flow. Nice work!',
            { userFlowName }
          )}
        </ZeroStatePanel>
      ) : (
        <IssuesPanel
          issues={filteredIssues}
          issuesProductSourceSlug={ProductSlugs.axeDevToolsExtension}
          onShare={userFlow.shared_with ? undefined : onShare}
          getIssueURL={getIssueURL}
          noScreenshotExplanation={noScreenshotExplanation}
        />
      )}

      {showExportModal && (
        <SimpleExportIssuesModal
          userFlow={userFlow}
          userFlowResults={results}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

export default UserFlowIssuesPage;
